import React from "react";

import { fetchApi } from "../../Network/NetworkService";
import { endpoint } from "../../Constants";
import AuthService from "../../Services/AuthService";
import { notif } from "../Ui/Notification";

import "../../Assets/Pages/dermatoSdr.css";

const Auth = new AuthService();

const LOCALES = [
  { key: "fr", label: "Français" },
  { key: "en", label: "Anglais" },
  { key: "es", label: "Espagnol" },
];

const EMPTY_SET = { fr: "", en: "", es: "" };

// Mode « avis en solo » : listes ordonnées d'URLs de cartes individuelles.
const emptySolo = () => ({ fr: [], en: [], es: [] });
const normalizeSolo = (raw) => {
  const out = emptySolo();
  LOCALES.forEach((l) => {
    if (raw && Array.isArray(raw[l.key])) {
      out[l.key] = raw[l.key].filter((v) => typeof v === "string" && v);
    }
  });
  return out;
};

// Réglages du bandeau d'avis Trustpilot du site patient
// (feeli-client TrustpilotContainer.jsx, servi via feeli-server /getSiteConf) :
// un visuel par langue pour le desktop, un par langue pour le mobile, et
// l'affichage du titre. Un champ vide = le site garde son visuel par défaut
// embarqué (l'anglais mobile retombe sur le français, comme dans le site).
class ReviewsWidgetSettings extends React.Component {
  constructor() {
    super();
    this.state = {
      loading: true,
      // Échec du chargement initial : l'état par défaut ne représente PAS la
      // config en base — le formulaire reste masqué et Enregistrer désactivé,
      // sinon un clic remplacerait la config existante par les défauts.
      loadError: false,
      saving: false,
      // clé de slot ("desktop.fr"...) -> upload en cours
      uploading: {},
      // Défauts = comportement historique du site : titre masqué sur desktop
      // (le visuel embarque le sien), affiché sur mobile, libellés du site.
      showTitleDesktop: false,
      showTitleMobile: true,
      title: { ...EMPTY_SET },
      desktop: { ...EMPTY_SET },
      mobile: { ...EMPTY_SET },
      // Avis en solo : slider de cartes individuelles à la place des visuels.
      soloEnabled: false,
      solo: emptySolo(),
    };
  }

  componentDidMount() {
    this.fetchConfig();
  }

  fetchConfig = () => {
    this.setState({ loading: true, loadError: false });
    fetchApi("/siteConf/reviews-widget")
      .then((res) => {
        const cfg = res.config || {};
        this.setState({
          loading: false,
          showTitleDesktop: cfg.showTitleDesktop === true,
          showTitleMobile: cfg.showTitleMobile !== false,
          title: { ...EMPTY_SET, ...(cfg.title || {}) },
          desktop: { ...EMPTY_SET, ...(cfg.desktop || {}) },
          mobile: { ...EMPTY_SET, ...(cfg.mobile || {}) },
          soloEnabled: cfg.soloEnabled === true,
          solo: normalizeSolo(cfg.solo),
        });
      })
      .catch(() => {
        notif.addNotification(
          "Erreur",
          "Erreur de récupération de la configuration",
          "warning",
        );
        this.setState({ loading: false, loadError: true });
      });
  };

  setUrl = (breakpoint, locale, value) => {
    this.setState((prev) => ({
      [breakpoint]: { ...prev[breakpoint], [locale]: value },
    }));
  };

  handleUpload = (breakpoint, locale, file) => {
    if (!file) return;
    const slotKey = breakpoint + "." + locale;
    this.setState((prev) => ({
      uploading: { ...prev.uploading, [slotKey]: true },
    }));

    const body = new FormData();
    body.append("file", file);

    fetch(endpoint + "/siteConf/reviews-widget/upload", {
      method: "POST",
      headers: { "x-access-token": Auth.getToken() },
      body,
    })
      .then((res) => res.json())
      .then((res) => {
        if (!res.success || !res.url) {
          return Promise.reject(res.message || "Échec du téléversement");
        }
        this.setUrl(breakpoint, locale, res.url);
        notif.addNotification(
          "Succès",
          "Image téléversée — pensez à enregistrer.",
          "success",
        );
      })
      .catch((err) => {
        notif.addNotification(
          "Erreur",
          typeof err === "string" ? err : "Échec du téléversement",
          "warning",
        );
      })
      .then(() => {
        this.setState((prev) => ({
          uploading: { ...prev.uploading, [slotKey]: false },
        }));
      });
  };

  // --- Avis en solo ---------------------------------------------------------

  // Upload d'une image de carte, promesse résolue avec l'URL S3.
  uploadSoloFile = (file) => {
    const body = new FormData();
    body.append("file", file);
    return fetch(endpoint + "/siteConf/reviews-widget/upload", {
      method: "POST",
      headers: { "x-access-token": Auth.getToken() },
      body,
    })
      .then((res) => res.json())
      .then((res) => {
        if (!res.success || !res.url) {
          return Promise.reject(res.message || "Échec du téléversement");
        }
        return res.url;
      });
  };

  // Plusieurs fichiers d'un coup : uploads EN SÉQUENCE pour préserver l'ordre
  // de sélection (l'ordre de la liste = l'ordre du slider sur le site).
  addSoloImages = (locale, fileList) => {
    const files = Array.from(fileList || []).slice(
      0,
      Math.max(0, 12 - this.state.solo[locale].length),
    );
    if (!files.length) return;
    const slotKey = "solo." + locale;
    this.setState((prev) => ({
      uploading: { ...prev.uploading, [slotKey]: true },
    }));

    files
      .reduce(
        (chain, file) =>
          chain.then((urls) =>
            this.uploadSoloFile(file).then((url) => urls.concat(url)),
          ),
        Promise.resolve([]),
      )
      .then((urls) => {
        this.setState((prev) => ({
          solo: {
            ...prev.solo,
            [locale]: prev.solo[locale].concat(urls).slice(0, 12),
          },
        }));
        notif.addNotification(
          "Succès",
          urls.length + " image(s) téléversée(s) — pensez à enregistrer.",
          "success",
        );
      })
      .catch((err) => {
        notif.addNotification(
          "Erreur",
          typeof err === "string" ? err : "Échec du téléversement",
          "warning",
        );
      })
      .then(() => {
        this.setState((prev) => ({
          uploading: { ...prev.uploading, [slotKey]: false },
        }));
      });
  };

  moveSolo = (locale, index, delta) => {
    this.setState((prev) => {
      const list = prev.solo[locale].slice();
      const target = index + delta;
      if (target < 0 || target >= list.length) return null;
      const [item] = list.splice(index, 1);
      list.splice(target, 0, item);
      return { solo: { ...prev.solo, [locale]: list } };
    });
  };

  removeSolo = (locale, index) => {
    this.setState((prev) => ({
      solo: {
        ...prev.solo,
        [locale]: prev.solo[locale].filter((_, i) => i !== index),
      },
    }));
  };

  save = () => {
    if (this.state.saving) return;
    this.setState({ saving: true });

    fetchApi("/siteConf/reviews-widget", {
      method: "PUT",
      body: JSON.stringify({
        showTitleDesktop: this.state.showTitleDesktop,
        showTitleMobile: this.state.showTitleMobile,
        title: this.state.title,
        desktop: this.state.desktop,
        mobile: this.state.mobile,
        soloEnabled: this.state.soloEnabled,
        solo: this.state.solo,
      }),
    })
      .then((res) => {
        const cfg = res.config || {};
        this.setState({
          saving: false,
          showTitleDesktop: cfg.showTitleDesktop === true,
          showTitleMobile: cfg.showTitleMobile !== false,
          title: { ...EMPTY_SET, ...(cfg.title || {}) },
          desktop: { ...EMPTY_SET, ...(cfg.desktop || {}) },
          mobile: { ...EMPTY_SET, ...(cfg.mobile || {}) },
          soloEnabled: cfg.soloEnabled === true,
          solo: normalizeSolo(cfg.solo),
        });
        notif.addNotification(
          "Succès",
          "Configuration enregistrée — visible sur le site immédiatement.",
          "success",
        );
      })
      .catch(() => {
        notif.addNotification("Erreur", "Échec de l'enregistrement", "warning");
        this.setState({ saving: false });
      });
  };

  renderSlot(breakpoint, locale, label) {
    const url = this.state[breakpoint][locale];
    const slotKey = breakpoint + "." + locale;
    const uploading = !!this.state.uploading[slotKey];
    const inputId = "reviews-upload-" + slotKey.replace(".", "-");

    return (
      <div
        key={slotKey}
        style={{
          border: "1px solid #e0e0e0",
          borderRadius: 10,
          padding: 14,
          flex: "1 1 260px",
          minWidth: 260,
        }}
      >
        <p style={{ fontWeight: 600, margin: "0 0 8px", color: "#14375F" }}>
          {label}
        </p>
        <div
          style={{
            background: "#f8f9fa",
            borderRadius: 8,
            minHeight: 90,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: 10,
            overflow: "hidden",
          }}
        >
          {url ? (
            <img
              src={url}
              alt={label}
              style={{ maxWidth: "100%", maxHeight: 110, display: "block" }}
            />
          ) : (
            <span style={{ color: "#999", fontSize: 13, padding: 10 }}>
              {"Visuel par défaut du site"}
            </span>
          )}
        </div>
        <input
          type="text"
          className="dermato-sdr-input"
          style={{ width: "100%", marginBottom: 8, boxSizing: "border-box" }}
          placeholder="URL de l'image (vide = visuel par défaut)"
          value={url}
          disabled={uploading}
          onChange={(e) => this.setUrl(breakpoint, locale, e.target.value)}
        />
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <input
            id={inputId}
            type="file"
            accept=".jpg,.jpeg,.png,.webp"
            style={{ display: "none" }}
            onChange={(e) => {
              this.handleUpload(breakpoint, locale, e.target.files[0]);
              e.target.value = "";
            }}
          />
          <label htmlFor={inputId} style={{ margin: 0 }}>
            <span
              className="dermato-sdr-btn dermatosdr-btn-link"
              style={{ cursor: "pointer", display: "inline-block" }}
            >
              {uploading ? "Envoi..." : "Téléverser une image"}
            </span>
          </label>
          {url && (
            <button
              className="dermato-sdr-btn dermatosdr-btn-link"
              style={{ color: "#c62828" }}
              disabled={uploading}
              onClick={() => this.setUrl(breakpoint, locale, "")}
            >
              {"Vider"}
            </button>
          )}
        </div>
      </div>
    );
  }

  renderSoloLocale(locale, label) {
    const list = this.state.solo[locale];
    const uploading = !!this.state.uploading["solo." + locale];
    const inputId = "reviews-solo-upload-" + locale;

    return (
      <div
        key={locale}
        style={{
          border: "1px solid #e0e0e0",
          borderRadius: 10,
          padding: 14,
          marginBottom: 14,
        }}
      >
        <p style={{ fontWeight: 600, margin: "0 0 8px", color: "#14375F" }}>
          {label}{" "}
          <span style={{ color: "#888", fontWeight: 400, fontSize: 13 }}>
            ({list.length}/12{list.length === 0 && locale !== "fr" ? " — reprendra les cartes françaises" : ""})
          </span>
        </p>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          {list.map((url, i) => (
            <div key={url + i} style={{ width: 150 }}>
              <div
                style={{
                  background: "#f8f9fa",
                  borderRadius: 8,
                  height: 110,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                }}
              >
                <img
                  src={url}
                  alt={label + " " + (i + 1)}
                  style={{ maxWidth: "100%", maxHeight: 110, display: "block" }}
                />
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  gap: 4,
                  marginTop: 4,
                }}
              >
                <button
                  className="dermato-sdr-btn dermatosdr-btn-link"
                  title="Avancer dans le slider"
                  disabled={uploading || i === 0}
                  onClick={() => this.moveSolo(locale, i, -1)}
                >
                  {"←"}
                </button>
                <button
                  className="dermato-sdr-btn dermatosdr-btn-link"
                  title="Reculer dans le slider"
                  disabled={uploading || i === list.length - 1}
                  onClick={() => this.moveSolo(locale, i, 1)}
                >
                  {"→"}
                </button>
                <button
                  className="dermato-sdr-btn dermatosdr-btn-link"
                  style={{ color: "#c62828" }}
                  title="Retirer cette carte"
                  disabled={uploading}
                  onClick={() => this.removeSolo(locale, i)}
                >
                  {"✕"}
                </button>
              </div>
            </div>
          ))}
          <div
            style={{
              width: 150,
              minHeight: 110,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "1px dashed #bbb",
              borderRadius: 8,
            }}
          >
            <input
              id={inputId}
              type="file"
              accept=".jpg,.jpeg,.png,.webp"
              multiple
              style={{ display: "none" }}
              onChange={(e) => {
                this.addSoloImages(locale, e.target.files);
                e.target.value = "";
              }}
            />
            <label htmlFor={inputId} style={{ margin: 0 }}>
              <span
                className="dermato-sdr-btn dermatosdr-btn-link"
                style={{ cursor: "pointer", display: "inline-block" }}
              >
                {uploading ? "Envoi..." : "+ Ajouter"}
              </span>
            </label>
          </div>
        </div>
      </div>
    );
  }

  renderSection(breakpoint, title, hint) {
    return (
      <div className="dermato-sdr-card" style={{ marginTop: 24 }}>
        <h3 className="dermato-sdr-card-title">{title}</h3>
        <p style={{ color: "#888", fontSize: 14, marginTop: -8 }}>{hint}</p>
        <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
          {LOCALES.map((l) => this.renderSlot(breakpoint, l.key, l.label))}
        </div>
      </div>
    );
  }

  render() {
    const {
      loading,
      loadError,
      saving,
      showTitleDesktop,
      showTitleMobile,
      title,
    } = this.state;
    const TITLE_PLACEHOLDERS = {
      fr: "Nos avis vérifiés",
      en: "Our verified reviews",
      es: "Nuestras opiniones verificadas",
    };

    return (
      <div className="dermato-sdr-container">
        <div className="dermato-sdr-header">
          <h2 className="dermato-sdr-title">
            {"Avis du site — visuels Trustpilot"}
          </h2>
          <button
            className="dermato-sdr-btn dermatosdr-btn-primary"
            onClick={this.save}
            disabled={saving || loading || loadError}
          >
            {saving ? "Enregistrement..." : "Enregistrer"}
          </button>
        </div>

        {loading ? (
          <div className="dermato-sdr-card">
            <div className="dermato-sdr-empty">
              <span className="loader" />
            </div>
          </div>
        ) : loadError ? (
          <div className="dermato-sdr-card">
            <div className="dermato-sdr-empty">
              <p style={{ marginBottom: 12 }}>
                {
                  "Impossible de charger la configuration actuelle — le formulaire est désactivé pour ne pas écraser la configuration existante."
                }
              </p>
              <button
                className="dermato-sdr-btn dermatosdr-btn-primary"
                onClick={this.fetchConfig}
              >
                {"Réessayer"}
              </button>
            </div>
          </div>
        ) : (
          <React.Fragment>
            <div className="dermato-sdr-card">
              <h3 className="dermato-sdr-card-title">Titre du composant</h3>
              <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
                {LOCALES.map((l) => (
                  <div key={l.key} style={{ flex: "1 1 220px", minWidth: 220 }}>
                    <p
                      style={{
                        fontWeight: 600,
                        margin: "0 0 6px",
                        color: "#14375F",
                      }}
                    >
                      {l.label}
                    </p>
                    <input
                      type="text"
                      className="dermato-sdr-input"
                      style={{ width: "100%", boxSizing: "border-box" }}
                      placeholder={TITLE_PLACEHOLDERS[l.key]}
                      maxLength={120}
                      value={title[l.key]}
                      disabled={saving}
                      onChange={(e) =>
                        this.setState({
                          title: { ...this.state.title, [l.key]: e.target.value },
                        })
                      }
                    />
                  </div>
                ))}
              </div>
              <p style={{ color: "#888", fontSize: 13, margin: "8px 0 14px" }}>
                {"Champ vide = libellé par défaut du site (le placeholder)."}
              </p>
              <div className="dermato-sdr-inline">
                <label className="dermato-sdr-checkbox">
                  <input
                    type="checkbox"
                    checked={showTitleDesktop}
                    disabled={saving}
                    onChange={(e) =>
                      this.setState({ showTitleDesktop: e.target.checked })
                    }
                  />
                  <span>{"Afficher le titre sur desktop"}</span>
                </label>
                <label className="dermato-sdr-checkbox">
                  <input
                    type="checkbox"
                    checked={showTitleMobile}
                    disabled={saving}
                    onChange={(e) =>
                      this.setState({ showTitleMobile: e.target.checked })
                    }
                  />
                  <span>{"Afficher le titre sur mobile"}</span>
                </label>
              </div>
              <p style={{ color: "#888", fontSize: 13, marginTop: 6 }}>
                {
                  "Par défaut : masqué sur desktop (le visuel embarque déjà son titre) et affiché sur mobile."
                }
              </p>
            </div>

            {this.renderSection(
              "desktop",
              "Visuels desktop (> 800 px)",
              "Grand visuel avec titre intégré. Conseillé : ≥ 2560 px de large pour rester net sur les écrans Retina. Vide = visuel par défaut du site (fr/en/es.png).",
            )}
            {this.renderSection(
              "mobile",
              "Visuels mobile (≤ 800 px)",
              "Bandeau de cartes qui défile automatiquement (affiché à 240 px de haut). Vide = visuel par défaut du site ; l'anglais sans visuel retombe sur le français.",
            )}

            <div className="dermato-sdr-card" style={{ marginTop: 24 }}>
              <h3 className="dermato-sdr-card-title">
                {"Avis en solo (slider)"}
              </h3>
              <p style={{ color: "#888", fontSize: 14, marginTop: -8 }}>
                {
                  "Cartes d'avis individuelles (captures Trustpilot une par une) affichées À LA PLACE des visuels desktop/mobile ci-dessus, dans un slider à flèches comme le widget Trustpilot : une carte d'avance par clic, 5 cartes visibles sur desktop (3 sur tablette, 1 sur mobile). Avec 5 cartes ou moins, pas de slider sur desktop : rangée statique centrée, sans flèches. L'ordre des vignettes = l'ordre d'affichage (flèches ← → pour réordonner). Une langue sans carte reprend les cartes françaises. Conseillé : captures de même largeur, ~1200 px."
                }
              </p>
              <label className="dermato-sdr-checkbox" style={{ marginBottom: 14 }}>
                <input
                  type="checkbox"
                  checked={this.state.soloEnabled}
                  disabled={saving}
                  onChange={(e) =>
                    this.setState({ soloEnabled: e.target.checked })
                  }
                />
                <span>
                  {"Activer le slider d'avis en solo sur le site"}
                </span>
              </label>
              {this.state.soloEnabled &&
                !this.state.solo.fr.length &&
                !this.state.solo.en.length &&
                !this.state.solo.es.length && (
                  <p style={{ color: "#c62828", fontSize: 13, marginTop: -6 }}>
                    {
                      "Aucune carte : le site gardera les visuels classiques tant qu'au moins une carte n'est pas ajoutée."
                    }
                  </p>
                )}
              {LOCALES.map((l) => this.renderSoloLocale(l.key, l.label))}
            </div>
          </React.Fragment>
        )}
      </div>
    );
  }
}

export default ReviewsWidgetSettings;
